import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import type { FeaturedLocation } from '../lib/database.types';

export default function FeaturedLocations() {
  const [items, setItems] = useState<FeaturedLocation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('ga_featured_locations')
        .select('*')
        .eq('is_active', true)
        .order('position');
      setItems((data as FeaturedLocation[]) ?? []);
      setLoading(false);
    })();
  }, []);

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 56, paddingBottom: 48 }}>
        <div className="container">
          <div className="eyebrow" style={{ marginBottom: 22 }}>Featured locations · {items.length} curated</div>
          <h1 className="display" style={{ fontSize: 'clamp(48px, 7vw, 96px)', margin: 0 }}>
            A handful of our <span className="serif-italic" style={{ color: 'var(--wine)' }}>favorite rooms.</span>
          </h1>
          <p style={{ color: 'var(--ink-soft)', fontSize: 'clamp(15px, 2vw, 17px)', marginTop: 16, maxWidth: 640 }}>
            Hand-picked NYC spots we love hosting teams at. Sign up to see live offers, available dates, and book.
          </p>
        </div>
      </section>

      <section style={{ paddingBottom: 120 }}>
        <div className="container">
          {loading ? (
            <div style={{ padding: 80, textAlign: 'center', color: 'var(--ink-soft)' }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: 60, borderRadius: 18, background: 'var(--card)', textAlign: 'center' }}>
              <div className="display" style={{ fontSize: 24 }}>Nothing featured right now.</div>
              <p className="muted" style={{ marginTop: 8 }}>
                <Link to="/request-location" style={{ textDecoration: 'underline' }}>Request a spot →</Link>
              </p>
            </div>
          ) : (
            <>
              <div className="r-grid-3">
                {items.map((loc) => <FeaturedCard key={loc.id} loc={loc} />)}
              </div>
              <div style={{ marginTop: 48, display: 'flex', justifyContent: 'center' }}>
                <Link to="/signup" className="btn btn-primary">View all locations →</Link>
              </div>
            </>
          )}

          <div style={{ marginTop: 64, padding: 32, borderRadius: 18, background: 'var(--card)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div className="display" style={{ fontSize: 24, margin: 0 }}>Ready to book?</div>
              <p className="muted" style={{ margin: '6px 0 0', maxWidth: 540 }}>
                Sign up with your work email to see live offers, dates, and pricing across our full network.
              </p>
            </div>
            <Link to="/signup" className="btn btn-primary">Sign up →</Link>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

function FeaturedCard({ loc }: { loc: FeaturedLocation }) {
  return (
    <Link to={`/featured-locations/${loc.slug}`} className="bar-card" style={{ display: 'block' }}>
      <div
        style={{
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: 16,
          overflow: 'hidden',
          // Image layered over a gradient so the card still looks right while
          // a photo upload is pending.
          background: loc.hero_image_url
            ? `url(${loc.hero_image_url}) center/cover no-repeat, linear-gradient(135deg, oklch(0.72 0.12 ${(loc.name.charCodeAt(0) * 7) % 360}), oklch(0.38 0.13 ${((loc.name.charCodeAt(0) * 7) + 30) % 360}))`
            : `linear-gradient(135deg, oklch(0.72 0.12 ${(loc.name.charCodeAt(0) * 7) % 360}), oklch(0.38 0.13 ${((loc.name.charCodeAt(0) * 7) + 30) % 360}))`,
          position: 'relative',
        }}
      >
        <span className="mono" style={{ position: 'absolute', top: 12, left: 12, background: 'var(--bg)', color: 'var(--ink)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 999 }}>
          Featured
        </span>
      </div>
      <div style={{ paddingTop: 16 }}>
        <div className="row-between">
          <h3 className="display" style={{ fontSize: 26, margin: 0 }}>{loc.name}</h3>
          {loc.neighborhood && (
            <span className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--ink-soft)', textTransform: 'uppercase' }}>{loc.neighborhood}</span>
          )}
        </div>
        {loc.address && (
          <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4, marginBottom: 12 }}>
            {loc.address}
          </div>
        )}
        {loc.description && (
          <p style={{ color: 'var(--ink-soft)', fontSize: 14, margin: '0 0 12px', lineHeight: 1.5 }}>{loc.description}</p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--line)' }}>
          <span className="display" style={{ fontSize: 22, color: 'var(--primary)' }}>View →</span>
        </div>
      </div>
    </Link>
  );
}
